/* ============================================================================
 * useDataLoading.ts - Data loading and initialization logic
 * ============================================================================ */

import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { ProjectData } from '@/shared/types/projectData';
import type { SnapshotHistory } from '@/shared/utils/snapshotHistory';
import { log } from '@/shared/utils/logger';

interface UseDataLoadingParams {
  setProjectData: Dispatch<SetStateAction<ProjectData | null>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string | null>>;
  setExpandedNodeIds: Dispatch<SetStateAction<Set<string>>>;
  snapshotHistory: MutableRefObject<SnapshotHistory>;
  updateUndoStateFromSnapshots: () => void;
}

/**
 * Hook for data loading and initialization.
 * Single-project loading is no longer used; MultiProjectContext owns server data hydration.
 */
export function useDataLoading({
  setIsLoading,
  setProjectData: _setProjectData,
  setError: _setError,
  setExpandedNodeIds: _setExpandedNodeIds,
  snapshotHistory: _snapshotHistory,
  updateUndoStateFromSnapshots: _updateUndoStateFromSnapshots
}: UseDataLoadingParams) {
  const loadProjectDataFromServer = useCallback(async () => {
    log.debug('useDataLoading', 'loadProjectDataFromServer called - skipping as we use MultiProjectContext now');
    setIsLoading(false);
  }, [setIsLoading]);

  return {
    loadProjectDataFromServer
  };
}
